import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

const ConnectionStatusContext = createContext({
  isOnline: true,
});

export const useConnectionStatus = () => useContext(ConnectionStatusContext);

export const ConnectionStatusProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const updateOnlineStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, []);

  return (
    <ConnectionStatusContext.Provider value={{ isOnline }}>
      {children}
    </ConnectionStatusContext.Provider>
  );
};

const UNSTABLE_THRESHOLD = 6;
const RESET_TIME = 20000; // 20 seconds
const STORAGE_KEY_COUNT = 'connection-switch-count';
const STORAGE_KEY_RESET_AT = 'connection-reset-at';

export const useUnstableConnection = () => {
  const [unstableConnection, setUnstableConnection] = useState(false);
  const switchCountRef = useRef<number>(0);
  const resetTimerRef = useRef<NodeJS.Timeout | null>(null);

  const scheduleReset = (resetAt: number) => {
    const delay = resetAt - Date.now();
    if (delay <= 0) {
      // Time already passed
      resetCounter();
    } else {
      resetTimerRef.current = setTimeout(() => {
        resetCounter();
        resetTimerRef.current = null;
      }, delay);
    }
  };

  const resetCounter = () => {
    switchCountRef.current = 0;
    localStorage.removeItem(STORAGE_KEY_COUNT);
    localStorage.removeItem(STORAGE_KEY_RESET_AT);
    setUnstableConnection(false);
  };

  useEffect(() => {
    const savedCount = localStorage.getItem(STORAGE_KEY_COUNT);
    const savedResetAt = localStorage.getItem(STORAGE_KEY_RESET_AT);

    if (savedCount) {
      const count = parseInt(savedCount, 10);
      switchCountRef.current = count;
      setUnstableConnection(count >= UNSTABLE_THRESHOLD);
    }

    if (savedResetAt) {
      const resetAt = parseInt(savedResetAt, 10);
      scheduleReset(resetAt);
    }
  }, []);

  useEffect(() => {
    const handleConnectionChange = () => {
      const now = Date.now();

      switchCountRef.current += 1;
      localStorage.setItem(STORAGE_KEY_COUNT, String(switchCountRef.current));

      if (switchCountRef.current >= UNSTABLE_THRESHOLD) {
        setUnstableConnection(true);
      }

      const resetAt = now + RESET_TIME;
      localStorage.setItem(STORAGE_KEY_RESET_AT, String(resetAt));

      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }

      scheduleReset(resetAt);
    };

    window.addEventListener('online', handleConnectionChange);
    window.addEventListener('offline', handleConnectionChange);

    return () => {
      window.removeEventListener('online', handleConnectionChange);
      window.removeEventListener('offline', handleConnectionChange);
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  return unstableConnection;
};
